/**
 * The XP multiplier a user is ACTUALLY earning at, and what it is made of.
 *
 * Every display used to read `user.xpMultipliers` alone — the temporary
 * NFT/lootbox boosts — while the backend also adds the membership tier's
 * multiplier at payout time (addAccountLevelTransaction.ts). So a Legend
 * subscriber saw no multiplier at all until they redeemed a boost, and then saw
 * the boost's figure while earning that plus their tier. This is the one place
 * that answers the question, so the chip, the tooltip and the Cockpit bar can
 * never disagree with each other or with what the backend pays.
 *
 * Boosts stack ADDITIVELY, exactly as the backend does it: 1 + Σ(each − 1).
 */

import {useMemo} from "react";
import {useSelector} from "react-redux";
import {TierEntitlementType, XpMultiplier} from "@devour/client";
import {IStore} from "@/redux/defaultStore";
import {useGetUserProfile} from "@/hooks/useGetUserProfile";
import {useGetCurrentSubscriptionTier} from "@/hooks/useGetCurrentSubscriptionTier";

export interface XpMultiplierBreakdown {
    /** What they earn at right now, rounded — 1.1 + 1.5 is 1.6000000000000001 raw. */
    total: number;
    /** The membership tier's always-on multiplier, or 0 when they have none. */
    tierBoost: number;
    /** Tier name for labelling the always-on line (e.g. "Legend"). */
    tierName?: string;
    /** Unexpired temporary boosts, for listing with their expiry. */
    temporary: Array<XpMultiplier>;
    /** Soonest expiry among the temporary boosts, or null when none expire. */
    earliestExpiration: number | null;
}

/**
 * @param expiryEpoch bump to re-evaluate when a boost lapses, so a stacked user
 * drops from 2.0x to 1.5x at that moment rather than at the next profile refetch.
 */
export function useGetXpMultiplier(expiryEpoch = 0): XpMultiplierBreakdown {
    const fullToken = useSelector((store: IStore) => store.authStore.fullToken);
    const {data: userProfile} = useGetUserProfile(fullToken);
    const {currentTier} = useGetCurrentSubscriptionTier(fullToken);

    /* Entitlement first, then the legacy flat field — the precedence tierXpMultiplier uses. */
    const tierMultiplier = currentTier?.entitlements
        ?.find(e => e.type === TierEntitlementType.XP_MULTIPLIER)?.multiplier
        ?? currentTier?.xpMultiplier ?? 1;
    const tierBoost = tierMultiplier > 1 ? tierMultiplier - 1 : 0;

    /*
     * `/users/profile` also SYNTHESIZES the tier's multiplier into this array
     * (get_profile.ts) so Electron and Overwolf, which read no tier of their own,
     * still see it. Counting BOTH that entry and `tierBoost` showed a Legend
     * 2.0x while they earned 1.5x, so the synthesized entry is dropped here and
     * the tier is taken from `currentTier` — the same source the tier badge and
     * the avatar ring use, so a member who is shown Legend is never shown no
     * multiplier. Deriving the total from the array instead made exactly that
     * happen: the profile stopped injecting and the chip vanished while the
     * badge still said Legend.
     *
     * Matched on the exact string get_profile builds; keep the two in step.
     */
    const syntheticTierName = currentTier?.name
        ? `${tierMultiplier}x ${currentTier.name} subscription`
        : null;

    const temporary = useMemo(
        () => userProfile?.user?.xpMultipliers?.filter(doc =>
            doc.expiresAt >= Date.now() && doc.name !== syntheticTierName) ?? [],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [userProfile?.user?.xpMultipliers, syntheticTierName, expiryEpoch],
    );

    const total = useMemo(() => {
        const stacked = temporary.reduce((sum, doc) => sum + (doc.amount - 1), 1 + tierBoost);
        return Math.round(stacked * 100) / 100;
    }, [temporary, tierBoost]);

    const earliestExpiration = temporary.length
        ? Math.min(...temporary.map(m => m.expiresAt))
        : null;

    return {
        total,
        tierBoost,
        tierName: tierBoost > 0 ? currentTier?.name : undefined,
        temporary,
        earliestExpiration,
    };
}
